import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/24/solid';
import { countries } from '@shared/constants';
import { useState } from 'react';
import styles from './flags.module.css';

interface CountrySelectorProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  touched?: boolean;
  label?: string;
}

export const CountrySelector = ({
  value,
  onChange,
  onBlur,
  error,
  touched,
  label = 'Country of Residence',
}: CountrySelectorProps) => {
  const [query, setQuery] = useState('');

  const handleChange = (nextValue: string | null) => {
    // Headless UI Combobox can emit `null` when selection is cleared.
    // Keep this component's external API `string`-only.
    if (nextValue === null) return;
    onChange(nextValue);
  };

  const filteredCountries =
    query === ''
      ? countries
      : countries.filter((country) => {
          return country.name.toLowerCase().includes(query.toLowerCase());
        });

  const shouldShowError = touched && !value && error;

  return (
    <fieldset className="fieldset">
      <legend className="fieldset-legend input-label">{label}</legend>
      <Combobox
        value={value}
        onChange={handleChange}
        onClose={() => {
          setQuery('');
          onBlur?.();
        }}
        immediate={true}
      >
        <div className="relative">
          <div
            className={`input input-dropdown flex items-center p-0 ${
              shouldShowError
                ? '!border-[var(--color-input-error)]'
                : 'focus-within:border-[var(--color-input-border-focus)]'
            }`}
          >
            <ComboboxInput
              className="flex-1 pl-2 py-2 focus:outline-none [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
              displayValue={(iso: string | null) => {
                if (!iso) return '';
                const country = countries.find((c) => c.iso === iso);
                return country ? country.name : '';
              }}
              onChange={(event) => setQuery(event.target.value)}
              type="search"
              data-lpignore="true"
              data-form-type="other"
              autoComplete="off"
            />
            <ComboboxButton className="pr-1">
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </ComboboxButton>
          </div>

          <ComboboxOptions className="absolute border border-[var(--color-dropdown-options-border)] shadow-sm bg-[var(--color-text-input-background)] z-10 mt-1 max-h-60 w-full overflow-auto rounded-md py-1 focus:outline-none">
            {filteredCountries.length === 0 && query !== '' ? (
              <div className="relative cursor-default select-none px-4 py-2 text-default">
                Nothing found.
              </div>
            ) : (
              filteredCountries.map((country) => (
                <ComboboxOption
                  key={country.iso}
                  value={country.iso}
                  className={({ focus }) =>
                    `relative text-default text-sm cursor-pointer select-none py-2 pl-3 ${
                      focus ? 'bg-[var(--color-selected-option-background)]' : ''
                    }`
                  }
                >
                  {({ selected }) => (
                    <div className="flex items-center gap-2">
                      <div className={`${styles.flag} ${styles[country.iso.toLowerCase()]}`} />
                      <span
                        className={`block text-wrap  ${selected ? 'font-medium' : 'font-normal'}`}
                      >
                        {country.name}
                      </span>
                    </div>
                  )}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
      {shouldShowError && <span className="input-error">{error}</span>}
    </fieldset>
  );
};
